import { RouteProp } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useResource from '../../../../../hooks/api/useResource';
import {
    PrimaryRecordAllergy,
    PrimaryRecordCondition,
    PrimaryRecordImaging,
    PrimaryRecordMedication,
    PrimaryRecordProcedure,
    PrimaryRecordVaccination,
    PrimaryRecordVisit,
} from '../../../../../primary-record-types/MedicalDataTypes';
import { getIntl } from '../../../../../utils/intl';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import { getCategoryLabel } from '../../../../../utils/resourceUtils/messages';
import ConditionForm from '../../../../shared/ResourceForms/ConditionForm';
import AllergyForm from '../../../../shared/ResourceForms/AllergyForm';
import MedicationForm, { isEndDateInPast } from '../../../../shared/ResourceForms/MedicationForm';
import VisitForm from '../../../../shared/ResourceForms/VisitForm';
import { useConfirmBackNavigationOnDirtyForm } from '../../../../shared/FormExitValidation';
import { getEditDefaultResource } from './getEditDefaultResource';
import VaccinationForm from '../../../../shared/ResourceForms/VaccinationForm';
import ProcedureForm from '../../../../shared/ResourceForms/ProcedureForm';
import ImagingForm from '../../../../shared/ResourceForms/ImagingForm';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import EditVisitSeriesPrompt from './EditVisitSeriesPrompt';
import EditMedicationGroupPrompt, { MedicationGroupScope } from './EditMedicationGroupPrompt';
import { MedicalDataTypes } from '../../../../../primary-record-types';

const intl = getIntl('edit-resource', {
    header: 'Edit {category}',
});

export type EditResourceScreenOwnProps = {};

export type EditResourceScreenProps = EditResourceScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'edit-data'>;
};

const EditResourceScreen: React.FC<EditResourceScreenProps> = (props) => {
    const navigation = useProfilesTabNavigation();
    const goBack = useGoBack();
    useConfirmBackNavigationOnDirtyForm(navigation);
    const { updateResource, isUpdatingResource, isLoadingResource, resource } = useResource({
        resourceId: props.route.params.resourceId,
        medicalProfileId: props.route.params.profileId,
        resourceCategory: props.route.params.category,
        onUpdated: () => {
            goBack();
        },
        relatedResourceCategories: [],
    });
    useSetHeaderTitle(intl('header', { category: getCategoryLabel(props.route.params.category) }));

    // A recurring visit prompts for edit scope (this / following / all) before saving.
    const visitInSeries =
        resource?.resource.type === 'visit' && resource.resource.recurrenceGroupId ? resource.resource : null;
    const [isSeriesEditPromptOpen, setIsSeriesEditPromptOpen] = useState(false);
    const [pendingVisitUpdate, setPendingVisitUpdate] = useState<{
        resourceUpdate: Partial<MedicalDataTypes.PrimaryRecordVisit>;
        imageUpdates: Parameters<typeof updateResource>[0]['imageUpdates'];
    } | null>(null);

    // A de-duplicated medication is one row backed by several entries, and a save only writes to
    // the one being edited. Changing the active status asks whether to carry it to the rest.
    const medicationGroupIds = useMemo(
        () => (resource?.resource.type === 'medication' ? resource.groupedResources?.map((_) => _.id) ?? [] : []),
        [resource],
    );
    const [isMedicationGroupPromptOpen, setIsMedicationGroupPromptOpen] = useState(false);
    const [pendingMedicationIsActive, setPendingMedicationIsActive] = useState(false);
    const [applyingMedicationScope, setApplyingMedicationScope] = useState<MedicationGroupScope | null>(null);
    const medicationGroupChoice = useRef<((scope: MedicationGroupScope) => void) | null>(null);
    const hasStartedGroupUpdate = useRef(false);

    // A successful save navigates away, so the prompt only needs dismissing when the save fails -
    // otherwise it would sit there reporting progress that has already stopped.
    useEffect(() => {
        if (!applyingMedicationScope) return;
        if (isUpdatingResource) {
            hasStartedGroupUpdate.current = true;
            return;
        }
        if (hasStartedGroupUpdate.current) {
            hasStartedGroupUpdate.current = false;
            setApplyingMedicationScope(null);
            setIsMedicationGroupPromptOpen(false);
        }
    }, [applyingMedicationScope, isUpdatingResource]);

    const confirmMedicationGroupScope = useCallback<
        NonNullable<React.ComponentProps<typeof MedicationForm>['confirmBeforeSubmit']>
    >(
        (data) => {
            const isActiveChanged =
                resource?.resource.type === 'medication' && data.resourceUpdate.isActive !== resource.resource.isActive;

            // Saving an active medication whose end date has passed is confirmed separately, and
            // that confirmation promises it will be stored as inactive - the server deactivates it
            // as soon as it lands. Offering to carry "active" to the group here would activate
            // every other entry and leave the edited one inactive, the opposite of what both
            // prompts said.
            const willDeactivateOnSave =
                !!data.resourceUpdate.isActive && isEndDateInPast(data.resourceUpdate.endDateTime);

            if (!isActiveChanged || willDeactivateOnSave || !medicationGroupIds.length) {
                return Promise.resolve(data);
            }

            setPendingMedicationIsActive(!!data.resourceUpdate.isActive);
            setIsMedicationGroupPromptOpen(true);
            return new Promise((resolve) => {
                medicationGroupChoice.current = (scope) => {
                    medicationGroupChoice.current = null;
                    if (scope === 'cancel') {
                        setIsMedicationGroupPromptOpen(false);
                        return resolve(null);
                    }
                    // The prompt stays up and reports progress: propagating across a large group
                    // takes long enough that closing it would leave the screen looking idle.
                    setApplyingMedicationScope(scope);
                    resolve(scope === 'all-entries' ? { ...data, propagateStatusToGroup: true } : data);
                };
            });
        },
        [resource, medicationGroupIds],
    );

    const renderForm = () => {
        if (!resource) return null;

        const category = props.route.params.category;
        if (category === 'condition') {
            return (
                <ConditionForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        updateResource({
                            imageUpdates: data.imageUpdates,
                            resourceUpdate: data.resourceUpdate as PrimaryRecordCondition,
                        });
                    }}
                    isDisabled={isUpdatingResource}
                    isLoading={isUpdatingResource}
                    defaultImages={resource.images}
                    defaultResource={getEditDefaultResource<PrimaryRecordCondition>(resource.resource, 'condition')}
                    resourceId={props.route.params.resourceId}
                    onCancel={goBack}
                />
            );
        } else if (category === 'allergy') {
            return (
                <AllergyForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        updateResource({
                            imageUpdates: data.imageUpdates,
                            resourceUpdate: data.resourceUpdate as PrimaryRecordAllergy,
                        });
                    }}
                    isDisabled={isUpdatingResource}
                    isLoading={isUpdatingResource}
                    defaultImages={resource.images}
                    defaultResource={getEditDefaultResource<PrimaryRecordAllergy>(resource.resource, 'allergy')}
                    resourceId={props.route.params.resourceId}
                    onCancel={goBack}
                />
            );
        } else if (category === 'medication') {
            return (
                <MedicationForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        updateResource({
                            ...data,
                            resourceUpdate: data.resourceUpdate as PrimaryRecordMedication,
                        });
                    }}
                    confirmBeforeSubmit={confirmMedicationGroupScope}
                    isDisabled={isUpdatingResource}
                    isLoading={isUpdatingResource}
                    defaultImages={resource.images}
                    defaultResource={getEditDefaultResource<PrimaryRecordMedication>(resource.resource, 'medication')}
                    resourceId={props.route.params.resourceId}
                    onCancel={goBack}
                />
            );
        } else if (category === 'visit') {
            return (
                <VisitForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        // For a series member, ask which occurrences the edit applies to first.
                        if (visitInSeries) {
                            setPendingVisitUpdate({
                                resourceUpdate: data.resourceUpdate as PrimaryRecordVisit,
                                imageUpdates: data.imageUpdates,
                            });
                            setIsSeriesEditPromptOpen(true);
                            return;
                        }
                        updateResource({
                            imageUpdates: data.imageUpdates,
                            resourceUpdate: data.resourceUpdate as PrimaryRecordVisit,
                        });
                    }}
                    isDisabled={isUpdatingResource}
                    isLoading={isUpdatingResource}
                    defaultImages={resource.images}
                    defaultResource={getEditDefaultResource<PrimaryRecordVisit>(resource.resource, 'visit')}
                    resourceId={props.route.params.resourceId}
                    onCancel={goBack}
                />
            );
        } else if (category === 'vaccination') {
            return (
                <VaccinationForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        updateResource({
                            imageUpdates: data.imageUpdates,
                            resourceUpdate: data.resourceUpdate as PrimaryRecordVaccination,
                        });
                    }}
                    isDisabled={isUpdatingResource}
                    isLoading={isUpdatingResource}
                    defaultImages={resource.images}
                    defaultResource={getEditDefaultResource<PrimaryRecordVaccination>(resource.resource, 'vaccination')}
                    resourceId={props.route.params.resourceId}
                    medicalProfileId={props.route.params.profileId}
                    onCancel={goBack}
                />
            );
        } else if (category === 'procedure') {
            return (
                <ProcedureForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        updateResource({
                            imageUpdates: data.imageUpdates,
                            resourceUpdate: data.resourceUpdate as PrimaryRecordProcedure,
                        });
                    }}
                    isDisabled={isUpdatingResource}
                    isLoading={isUpdatingResource}
                    defaultImages={resource.images}
                    defaultResource={getEditDefaultResource<PrimaryRecordProcedure>(resource.resource, 'procedure')}
                    resourceId={props.route.params.resourceId}
                    onCancel={goBack}
                />
            );
        } else if (category === 'imaging') {
            return (
                <ImagingForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        updateResource({
                            imageUpdates: data.imageUpdates,
                            resourceUpdate: data.resourceUpdate as PrimaryRecordImaging,
                        });
                    }}
                    isDisabled={isUpdatingResource}
                    isLoading={isUpdatingResource}
                    defaultImages={resource.images}
                    defaultResource={getEditDefaultResource<PrimaryRecordImaging>(resource.resource, 'imaging')}
                    resourceId={props.route.params.resourceId}
                    onCancel={goBack}
                />
            );
        }
    };

    return (
        <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder omitChatButton>
            {visitInSeries ? (
                <EditVisitSeriesPrompt
                    isOpen={isSeriesEditPromptOpen}
                    setIsOpen={setIsSeriesEditPromptOpen}
                    profileId={props.route.params.profileId}
                    visit={visitInSeries}
                    pendingResourceUpdate={pendingVisitUpdate?.resourceUpdate ?? null}
                    onUpdateSingle={() => {
                        if (!pendingVisitUpdate) return;
                        updateResource(pendingVisitUpdate);
                    }}
                    onSeriesUpdated={goBack}
                />
            ) : null}
            {medicationGroupIds.length ? (
                <EditMedicationGroupPrompt
                    isOpen={isMedicationGroupPromptOpen}
                    otherEntryCount={medicationGroupIds.length}
                    isActive={pendingMedicationIsActive}
                    isSubmitting={!!applyingMedicationScope}
                    submittingEntryCount={applyingMedicationScope === 'all-entries' ? medicationGroupIds.length + 1 : 1}
                    onSelect={(scope) => medicationGroupChoice.current?.(scope)}
                />
            ) : null}
            <AnimateLoadedContent isLoading={isLoadingResource || !resource}>{() => renderForm()}</AnimateLoadedContent>
        </ProfileHeaderContainer>
    );
};

export default React.memo(EditResourceScreen);
